import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { getApiErrorMessage } from '../lib/apiErrors';
import { queryKeys } from '../lib/queryKeys';
import type { DashboardOverviewResponse, TicketTrendDataPoint } from '../types/tickets';

// Get dashboard overview data
export function useDashboardOverview() {
  return useQuery({
    queryKey: queryKeys.reports.dashboard(),
    queryFn: async () => {
      const response = await apiService.getDashboardOverview();
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch dashboard overview'));
      }
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 5 * 60 * 1000, // Auto-refresh every 5 minutes
  });
}

// Get dashboard overview data for all user's organizations (unified multi-org view)
// Pass organizationIds to filter to specific organizations (empty = all user's orgs)
export function useAllOrganizationsDashboard(organizationIds?: string[]) {
  return useQuery({
    queryKey: queryKeys.reports.dashboardAllOrgs(organizationIds),
    queryFn: async () => {
      const response = await apiService.getDashboardOverviewAllOrganizations(organizationIds);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch dashboard overview'));
      }
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 5 * 60 * 1000, // Auto-refresh every 5 minutes
  });
}

// Get ticket trends for a specific date range
export function useTicketTrends(fromDate?: string, toDate?: string) {
  return useQuery({
    queryKey: queryKeys.reports.trends({ fromDate, toDate }),
    queryFn: async () => {
      const response = await apiService.getTicketTrends(fromDate, toDate);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch ticket trends'));
      }
      return response.data;
    },
    enabled: true,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

// Transform TicketTrendDataPoint[] to chart-friendly format
export function transformTicketTrendsForChart(data: TicketTrendDataPoint[]) {
  return data.map(point => ({
    name: point.label,
    date: point.date,
    created: point.ticketsCreated,
    completed: point.ticketsCompleted
  }));
}

// Transform dashboard activities for display
export function transformActivitiesForDisplay(activities: DashboardOverviewResponse['recentActivities']) {
  return activities.map(activity => ({
    id: activity.id,
    type: activity.type,
    action: activity.action,
    description: activity.description,
    timestamp: new Date(activity.timestamp).toLocaleString(),
    user: activity.userName || 'System',
    ticketId: activity.ticketId,
    ticketNumber: activity.ticketNumber,
    ticketSubject: activity.ticketSubject,
    organizationId: activity.organizationId,
    organizationName: activity.organizationName
  }));
}

/**
 * Tickets created in the range by category (Dashboard), own and rolled up with subcategories. Keeps the previous
 * report while a new range loads, so the card holds its frame instead of flashing.
 */
export function useCategoryReport(organizationId: string | undefined, fromDate: string, toDate: string) {
  return useQuery({
    queryKey: queryKeys.reports.categories(organizationId ?? '', fromDate, toDate),
    queryFn: async () => {
      const response = await apiService.getCategoryReport(organizationId!, fromDate, toDate);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to load the category report'));
      }
      return response.data;
    },
    enabled: !!organizationId,
    staleTime: 60 * 1000,
    placeholderData: keepPreviousData,
  });
}
